import eventLogger from "@/services/ApiLogger/EventLogger";
import {
  ArtificialIntelligenceConfiguration,
  EmailConfig,
  LanguageConfig as LanguagesConfig,
  LanguageShortModel,
  OAuthStatus
} from "@medapro-types/ConfigurationManager/ConfigurationManager";
import {
  MediaConverterPayload,
  MediaConverterProfile,
  MediaConverterProfileParameters,
  SmartSnippetsEntries
} from "@medapro-types/ConfigurationManager/components/media-converter-config";
import { UnitDetail, UnitTranslationDetail } from "@medapro-types/ConfigurationManager/components/unit-translation-config";
import {
  ConfigManagerActions,
  ConfigManagerGetters,
  ConfigManagerMutations,
  ConfigManagerState,
  ConfigurationManagerErrors,
  InitialDataHash
} from "@medapro-types/ConfigurationManager/configuration-manager-store";
import { EmailAuthType, ResponseStatus } from "@medapro-types/common/common-enum";
import alternativeArticleNoTypesApi from "@services/alternativeArticleNoTypes.js";
import articleStateApi from "@services/articleState";
import commonApi from "@services/common.js";
import configurationManagerApi from "@services/configurationManager";
import emailConfigurationApi from "@services/emailConfiguration";
import languageApi from "@services/languages";
import { Guid } from "@util/guid";
import { AxiosError } from "axios";
import { sha1 as hash } from "object-hash";
import catalogApi from "../../services/catalogs";
import { naturalSort } from "@util/naturalSort";

const defaultState = (): ConfigManagerState => ({
  runningPromises: [],
  saveMessageSeen: true,
  articleNoConfig: [],
  articleStateConfig: [],
  languagesConfig: {
    languages: [],
    isTranslationStatusEnabled: false
  },
  mediaConverterProfiles: null,
  mediaConverterProfileParameters: null,
  eMailConfig: null,
  mappingTargets: [],
  //ruleProfiles -  set of collection of rules for a target system
  //profileRules -  set of rules for a ruleprofile
  ruleProfiles: {},
  profileRules: {},
  selectedRuleProfileId: null,
  unitConfig: null,
  unitTranslations: null,
  selectedUnit: undefined,
  selectedUnitTranslation: [],
  nestedSections: ["ruleProfiles", "profileRules"],
  selectedMediaConverterProfile: null,
  smartSnippetsEntries: [],
  errors: {
    articleNoConfig: null,
    articleStateConfig: null,
    languagesConfig: null,
    mediaConverterProfile: null,
    eMailConfig: null,
    ruleProfiles: null,
    profileRules: null,
    unitTranslations: null,
    artificialIntelligence: null
  },
  apiErrors: {},
  initialDataHash: {
    articleNoConfig: null,
    articleStateConfig: null,
    languagesConfig: null,
    mediaConverterProfileParameters: null,
    eMailConfig: null,
    ruleProfiles: {},
    profileRules: {},
    unitConfig: null,
    unitTranslations: null,
    artificialIntelligence: null
  },
  deleted: {
    articleNoConfig: [],
    articleStateConfig: [],
    unitConfig: [],
    ruleProfiles: [],
    profileRules: [],
    unitTranslations: [],
    languagesConfig: []
  },
  selectableLanguages: [],
  isSaving: false,
  artificialIntelligence: null,
  oAuthStatus: null
});

const getters: ConfigManagerGetters = {
  hasUnsavedData: function (state: ConfigManagerState, getters: any) {
    const keys = Object.keys(state.initialDataHash) as (keyof InitialDataHash)[];
    return keys.some((x) => getters.isSectionModified(x));
  },
  isBusy: function (state: ConfigManagerState) {
    return !!state.runningPromises.length;
  },
  lastRunSuccess: function (state: ConfigManagerState) {
    const result = Object.values(state.errors).every((x) => !x);
    return result;
  },
  sectionHasError: function (state: ConfigManagerState) {
    return (section: string) => {
      return state.errors[section];
    };
  },
  isSectionModified: (state: ConfigManagerState) => (section: keyof InitialDataHash) => {
    if (state.nestedSections.includes(section)) {
      const sectionData = state[section] as any;
      const keys = Object.keys(sectionData);
      const hashData = state.initialDataHash[section];
      if (hashData && typeof hashData === "object") {
        const hasAnyModifiedChildSection = keys.some((x) => sectionData[x] && hashData[x]);
        return hasAnyModifiedChildSection;
      }
    }
    return state.initialDataHash[section] && state[section] && hash(state[section]) !== state.initialDataHash[section];
  },
  isSaving: function (state: ConfigManagerState) {
    return state.isSaving;
  },
  fieldHasAPIError: function (state: ConfigManagerState) {
    return (section: string, field: string) => {
      return state.apiErrors[section] && !!state.apiErrors[section][field];
    };
  }
};
const mutations: ConfigManagerMutations = {
  "reset-state"(state) {
    Object.assign(state, defaultState());
  },
  "set-initial-hash": function (state: ConfigManagerState, args: { section: keyof InitialDataHash; childSectionId?: any; data: any }) {
    if (state.nestedSections.includes(args.section)) {
      const sectionData = state.initialDataHash[args.section];

      if (sectionData && typeof sectionData === "object" && !Array.isArray(sectionData)) {
        if (sectionData[args.childSectionId] !== undefined) {
          sectionData[args.childSectionId] = false; // Assigning a boolean value
        }
      }
    } else {
      state.initialDataHash[args.section] = hash(args.data);
    }
  },
  "update-hash": function (state: ConfigManagerState, args: { section: keyof InitialDataHash; childSectionId?: any; data: any }) {
    if (state.nestedSections.includes(args.section)) {
      const sectionData = state.initialDataHash[args.section];
      sectionData[args.childSectionId] = true;
    }
  },
  "notification-seen": function (state: ConfigManagerState) {
    state.saveMessageSeen = true;
  },
  "add-running-promise": function (state: ConfigManagerState, promise: Promise<any>) {
    state.runningPromises.push(promise);
    promise.finally(() => {
      const index = state.runningPromises.findIndex((p) => p === promise);
      if (index !== -1) {
        state.runningPromises.splice(index, 1);
      }
    });
  },
  "reset-running-promises": function (state: ConfigManagerState) {
    state.runningPromises = [];
  },
  "set-article-no-config": function (state: ConfigManagerState, args: any) {
    state.articleNoConfig = args;
  },
  "set-article-state-config": function (state: ConfigManagerState, args: any) {
    state.articleStateConfig = args;
  },
  "set-languages-config": function (state: ConfigManagerState, args: any) {
    state.languagesConfig = args;
  },
  "set-media-converter-profiles": function (state: ConfigManagerState, args: MediaConverterProfile[] | null) {
    state.mediaConverterProfiles = args;
  },
  "set-selected-media-converter-profile": function (state: ConfigManagerState, args: MediaConverterProfile | null) {
    state.selectedMediaConverterProfile = args;
  },
  "set-media-converter-profile-parameters": function (state: ConfigManagerState, args: MediaConverterProfileParameters | null) {
    state.mediaConverterProfileParameters = args;
  },
  "set-smart-snippets-entries": function (state: ConfigManagerState, args: any) {
    state.smartSnippetsEntries = args;
  },
  "set-e-mail-config": function (state: ConfigManagerState, args: any) {
    state.eMailConfig = args;
  },
  "set-mapping-targets": function (state: ConfigManagerState, args: any) {
    state.mappingTargets = args;
  },
  "set-rule-profiles": function (state: ConfigManagerState, args: any) {
    state.ruleProfiles[args.targetSystemId] = args.profiles;
  },
  "set-profile-rules": function (state: ConfigManagerState, args: any) {
    state.profileRules[args.ruleProfileId] = args.rules;
  },
  "set-selected-rule-profile-id": function (state: ConfigManagerState, args: any) {
    state.selectedRuleProfileId = args;
  },
  "set-unit-config": function (state: ConfigManagerState, args: any) {
    state.unitConfig = args;
  },
  "set-unit-translations-config": function (state: ConfigManagerState, args: any) {
    state.unitTranslations = args;
  },
  "set-selected-unit": function (state: ConfigManagerState, args: UnitDetail) {
    state.selectedUnit = args;
  },
  "set-selected-unit-translations": function (state: ConfigManagerState, args: UnitTranslationDetail[]) {
    state.selectedUnitTranslation = args;
  },
  "update-unit-systems-ids": function (state: ConfigManagerState, args: any) {
    const offSet = state.unitConfig ? state.unitConfig.filter((item) => item.isLocked).length : 0;
    Object.keys(args).forEach((indexString) => {
      const index = Number(indexString);
      const item = state.unitConfig?.[index + offSet];
      if (item) {
        item.unitSystemId = args[index];
      }
    });
  },
  "update-unit-translations-ids": function (state: ConfigManagerState, args: any) {
    Object.keys(args).forEach((indexString) => {
      const index = Number(indexString);
      const item = state.unitTranslations?.[index];
      if (item) {
        item.unitSystemSubstitutionId = args[index];
      }
    });
  },
  "mark-as-deleted": function (state: ConfigManagerState, args: { section: string; value: Guid | number }) {
    if (Array.isArray(args.value)) {
      const items = state.deleted[args.section];
      items.push(...args.value);
    } else {
      state.deleted[args.section].push(args.value);
    }
  },
  "reset-deleted-section": function (state: ConfigManagerState, section: string) {
    state.deleted[section] = [];
  },
  "set-section-error": function (state: ConfigManagerState, args: { section: string; error: any }) {
    const errors = Object.assign({}, state.errors);
    errors[args.section] = args.error;
    state.errors = errors;
  },
  "update-alternative-article-no-type-ids": function (state: ConfigManagerState, args: any) {
    Object.keys(args).forEach((indexString) => {
      const index = Number(indexString);
      const item = state.articleNoConfig?.[index];
      if (item) {
        item.alternativeArticleNoTypeId = args[index];
      }
    });
  },
  "update-article-state-ids": function (state: ConfigManagerState, args: any) {
    Object.keys(args).forEach((indexString) => {
      const index = Number(indexString);
      const item = state.articleStateConfig?.[index];
      if (item) {
        item.articleStateId = args[index];
      }
    });
  },
  "update-media-converter-profile": function (state: ConfigManagerState, profile: any) {
    if (state.selectedMediaConverterProfile) {
      state.selectedMediaConverterProfile = profile;
    }
  },
  "update-media-converter-profile-name": function (state: ConfigManagerState, args: any) {
    if (state.selectedMediaConverterProfile) {
      state.selectedMediaConverterProfile.name = args.name;
    }
    state.mediaConverterProfiles =
      state.mediaConverterProfiles?.map((profile) => (profile.mediaConversionProfileId === args.profileId ? { ...profile, name: args.name } : profile)) || null;
  },
  "update-deleted-media-converter-profile-id": function (state: ConfigManagerState, profileId: string) {
    const index = state.mediaConverterProfiles?.findIndex((p) => p.mediaConversionProfileId === profileId);
    if (index !== undefined && index !== -1) {
      state.mediaConverterProfiles?.splice(index, 1);
    }
  },
  "set-selectable-languages": function (state: ConfigManagerState, args: string[]) {
    state.selectableLanguages = args;
  },
  "notification-show": function (state: ConfigManagerState) {
    state.saveMessageSeen = false;
  },
  "reset-errors": function (state: ConfigManagerState) {
    const keys: (keyof ConfigurationManagerErrors)[] = Object.keys(state.errors) as (keyof ConfigurationManagerErrors)[];
    keys.forEach((key) => (state.errors[key] = null));
  },
  "reset-field-error": function (state: ConfigManagerState, args: any) {
    if (state.apiErrors[args.section] && state.apiErrors[args.section][args.field]) {
      state.apiErrors[args.section][args.field] = null;
    }
  },
  "set-is-saving": function (state: ConfigManagerState, args: any) {
    state.isSaving = args;
  },
  "set-artificial-intelligence": function (state: ConfigManagerState, args: any) {
    state.artificialIntelligence = args;
  },
  "set-field-error": function (state: ConfigManagerState, args: { section: string; field: string; error: any }) {
    const errors = Object.assign({}, state.apiErrors);
    errors[args.section] = errors[args.section] || {};
    errors[args.section][args.field] = args.error;
    state.apiErrors = errors;
  },
  "set-oauth-status": function (state: ConfigManagerState, args: OAuthStatus | null) {
    state.oAuthStatus = args;
  }
};
const actions: ConfigManagerActions = {
  resetRunningPromisesIfResolved: async function ({ state, commit }) {
    await Promise.all(state.runningPromises);
    commit("reset-running-promises");
  },
  loadAllSettings: async function ({ dispatch }) {
    dispatch("loadArticleNumberSources");
    dispatch("loadArticleStateConfig");
    dispatch("loadLanguagesConfig");
    dispatch("loadMediaConverterProfiles");
    dispatch("loadEMailSettings");
    dispatch("loadRuleProfiles");
    dispatch("loadProfileRules");
    dispatch("loadUnitConfig");
    dispatch("loadSelectableLanguages");
    dispatch("loadMappingTargets");

    dispatch("resetRunningPromisesIfResolved");
  },
  loadMappingTargets: async function ({ state, commit }) {
    if (!state.mappingTargets?.length) {
      const promise = catalogApi.getMappingTargets({
        catalogType: 1
      });
      commit("add-running-promise", promise);
      const fetchedResult = await promise;
      commit("set-initial-hash", { data: fetchedResult, section: "mappingTargets" });
      commit("set-mapping-targets", fetchedResult);
    }
  },
  loadArticleNumberSources: async function ({ state, commit, dispatch }) {
    if (!state.articleNoConfig?.length) {
      const promise = new Promise((res) => {
        res(alternativeArticleNoTypesApi.get());
      });
      commit("add-running-promise", promise);
      const fetchedResult = await promise;
      commit("set-initial-hash", { data: fetchedResult, section: "articleNoConfig" });
      commit("set-article-no-config", fetchedResult);
      await dispatch("resetRunningPromisesIfResolved");
    }
  },
  loadArticleStateConfig: async function ({ state, commit, dispatch }) {
    if (!state.articleStateConfig?.length) {
      const promise = articleStateApi.get();
      commit("add-running-promise", promise);
      const fetchedResult = await promise;
      commit("set-initial-hash", { data: fetchedResult, section: "articleStateConfig" });
      commit("set-article-state-config", fetchedResult);
      await dispatch("resetRunningPromisesIfResolved");
    }
  },
  loadLanguagesConfig: async function ({ state, commit, dispatch }) {
    if (!state.languagesConfig.languages.length) {
      const languagePromise = commonApi.getLanguages();
      const isTranslationStatusEnabledPromise = languageApi.getIsTranslationStatusEnabled();
      commit("add-running-promise", languagePromise);
      commit("add-running-promise", isTranslationStatusEnabledPromise);
      const languagesResult = await languagePromise;
      const isTranslationStatusEnabledResult = await isTranslationStatusEnabledPromise;
      const languagesConfig: LanguagesConfig = {
        languages: languagesResult,
        isTranslationStatusEnabled: isTranslationStatusEnabledResult
      };
      commit("set-initial-hash", { data: languagesConfig, section: "languagesConfig" });
      commit("set-languages-config", languagesConfig);
      await dispatch("resetRunningPromisesIfResolved");
    }
  },
  loadMediaConverterProfiles: async function ({ state, commit, dispatch }) {
    if (!state.mediaConverterProfiles?.length) {
      const promise = configurationManagerApi.getMediaConverterProfiles();
      commit("add-running-promise", promise);
      const fetchedResult: any = await promise;
      commit("set-initial-hash", { data: fetchedResult, section: "mediaConverterProfiles" });
      commit("set-media-converter-profiles", fetchedResult);
    }
    await dispatch("resetRunningPromisesIfResolved");
  },
  loadSmartSnippets: async function name({ state, commit, dispatch, rootState }) {
    if (!state.smartSnippetsEntries.length) {
      const promise = configurationManagerApi.getSmartSnippetsEntries(rootState.metadata?.contentLanguages?.enabled[0]?.languageCode ?? "de");
      commit("add-running-promise", promise);
      const fetchedResult: SmartSnippetsEntries = await promise;

      commit("set-smart-snippets-entries", fetchedResult);
      await dispatch("resetRunningPromisesIfResolved");
    }
  },
  loadMediaConverterProfileParameters: async function ({ commit, dispatch }, profileId: string) {
    const promise = configurationManagerApi.getMediaConverterProfileParameters(profileId);
    commit("add-running-promise", promise);
    const fetchedResult = await promise;
    commit("set-initial-hash", {
      data: { ...fetchedResult.profileParameters, FileNameSmartSnippets: fetchedResult.fileNameSmartSnippets },
      section: "mediaConverterProfileParameters"
    });
    commit("set-media-converter-profile-parameters", { ...fetchedResult.profileParameters, FileNameSmartSnippets: fetchedResult.fileNameSmartSnippets });
    await dispatch("resetRunningPromisesIfResolved");
  },
  loadEMailSettings: async function ({ state, commit, dispatch }) {
    if (!state.eMailConfig) {
      const promise = emailConfigurationApi.get();
      commit("add-running-promise", promise);
      const fetchedResult = await promise;
      commit("set-initial-hash", { data: fetchedResult, section: "eMailConfig" });
      commit("set-e-mail-config", fetchedResult);
      if (state.eMailConfig) {
        const config = state.eMailConfig as EmailConfig;
        if (config.authType === EmailAuthType.OAuth) {
          if (config?.tenantId && config?.clientId) {
            await dispatch("checkOAuthAuthentication");
          } else {
            commit("set-oauth-status", {
              status: ResponseStatus.Error,
              message: "UserNotAuthenticated"
            });
          }
        }
      }
      await dispatch("resetRunningPromisesIfResolved");
    }
  },
  loadRuleProfiles: async function ({ commit }, catalogMappingTargetId) {
    const promise = configurationManagerApi.getRuleProfiles(catalogMappingTargetId);
    const profiles = await promise;
    commit("set-initial-hash", { data: profiles, section: "ruleProfiles", childSectionId: catalogMappingTargetId });
    commit("set-rule-profiles", { targetSystemId: catalogMappingTargetId, profiles });
  },
  loadProfileRules: async function ({ commit, dispatch }, ruleProfileId) {
    const promise = await configurationManagerApi.getProfileRule(ruleProfileId);
    const rules = await promise;
    commit("set-initial-hash", { data: rules, section: "profileRules", childSectionId: ruleProfileId });
    commit("set-profile-rules", { ruleProfileId, rules });
    await dispatch("resetRunningPromisesIfResolved");
  },
  loadUnitConfig: async function ({ commit, dispatch }) {
    const promise: Promise<UnitDetail[]> = configurationManagerApi.getUnitSystems();
    commit("add-running-promise", promise);
    const fetchedResult = naturalSort(await promise, "name");
    const sortedResult = fetchedResult.sort((a, b) => {
      if (a.isLocked === b.isLocked) return 0;
      return a.isLocked ? -1 : 1;
    });
    commit("set-initial-hash", { data: sortedResult, section: "unitConfig" });
    commit("set-unit-config", sortedResult);
    await dispatch("resetRunningPromisesIfResolved");
  },
  loadUnitTranslations: async function ({ commit, dispatch }, unitSystemId) {
    const promise = configurationManagerApi.getUnitTranslations(unitSystemId);
    const fetchedResult = await promise;
    commit("set-initial-hash", { data: fetchedResult, section: "unitTranslations" });
    commit("set-unit-translations-config", fetchedResult);
    commit("set-selected-unit-translations", fetchedResult);
    await dispatch("resetRunningPromisesIfResolved");
  },
  loadSelectableLanguages: async function ({ state, commit, dispatch }) {
    if (!state.selectableLanguages.length) {
      const promise = languageApi.getAvailableLanguageCodes();
      commit("add-running-promise", promise);
      const fetchedResult = await promise;
      commit("set-selectable-languages", fetchedResult);
      await dispatch("resetRunningPromisesIfResolved");
    }
  },
  setArticleNumberSources: function ({ commit }, args?: any) {
    commit("set-article-no-config", args);
  },
  setArticleStateConfig: function ({ commit }, args?: any) {
    commit("set-article-state-config", args);
  },
  setLanguagesConfig: function ({ commit }, args: LanguagesConfig) {
    commit("set-languages-config", args);
  },
  setMediaConverterProfileParameters: function ({ commit }, args?: MediaConverterProfileParameters | null) {
    commit("set-media-converter-profile-parameters", args);
  },
  setSelectedMediaConverterProfile: function ({ commit }, args?: MediaConverterProfile | null) {
    commit("set-selected-media-converter-profile", args);
  },
  setEMailSettings: function ({ commit }, args?: any) {
    commit("set-e-mail-config", args);
  },
  setSelectedRuleProfileId: function ({ commit }, args?: any) {
    commit("set-selected-rule-profile-id", args);
  },
  setUnitConfig: function ({ commit }, args?: any) {
    commit("set-unit-config", args);
  },
  setUnitTranslations: function ({ commit }, args?: any) {
    commit("set-unit-translations-config", args);
  },
  setSelectedUnit: function ({ commit }, args?: any) {
    commit("set-selected-unit", args);
  },
  saveAllSettings: async function ({ dispatch, commit, getters }) {
    try {
      if (getters.hasUnsavedData) {
        commit("set-is-saving", true);
        commit("reset-errors");
        commit("notification-show");
        if (getters.isSectionModified("articleNoConfig")) {
          await dispatch("saveArticleNumberSources");
        }
        if (getters.isSectionModified("articleStateConfig")) {
          await dispatch("saveArticleStateConfig");
        }
        if (getters.isSectionModified("languagesConfig")) {
          await dispatch("saveLanguagesConfig");
        }
        if (getters.isSectionModified("mediaConverterProfileParameters")) {
          await dispatch("saveMediaConverterProfiles");
        }
        if (getters.isSectionModified("eMailConfig")) {
          await dispatch("saveEMailSettings");
        }
        if (getters.isSectionModified("profileRules")) {
          await dispatch("saveProfileRules");
        }
        if (getters.isSectionModified("unitTranslations")) {
          await dispatch("saveUnitTranslationsConfig");
        }
        if (getters.isSectionModified("unitConfig")) {
          await dispatch("saveUnitConfig");
        }
        if (getters.isSectionModified("artificialIntelligence")) {
          await dispatch("saveAISettings");
        }
        await dispatch("resetRunningPromisesIfResolved");
      }
    } catch (error) {
      console.error("save failed", error);
    } finally {
      commit("set-is-saving", false);
    }
  },
  saveArticleNumberSources: async function ({ state, commit }) {
    try {
      if (state.deleted.articleNoConfig.length) {
        await alternativeArticleNoTypesApi.delete(state.deleted.articleNoConfig);
      }
      const result = await alternativeArticleNoTypesApi.update(state.articleNoConfig);
      if (result) {
        commit("update-alternative-article-no-type-ids", result);
      }
      commit("set-initial-hash", { data: state.articleNoConfig, section: "articleNoConfig" });
    } catch (error) {
      commit("set-section-error", {
        section: "articleNoConfig",
        error: error
      });
    }
  },
  saveArticleStateConfig: async function ({ state, commit }) {
    try {
      const result = await articleStateApi.update(state.articleStateConfig);
      if (result) {
        commit("update-article-state-ids", result);
      }
      if (state.deleted.articleStateConfig.length) {
        await articleStateApi.delete(state.deleted.articleStateConfig);
      }
      commit("set-initial-hash", { data: state.articleStateConfig, section: "articleStateConfig" });
    } catch (error) {
      commit("set-section-error", {
        section: "articleStateConfig",
        error: error
      });
    }
  },
  saveLanguagesConfig: async function ({ state, commit, dispatch }) {
    const logger = eventLogger.beginContext("saveLanguagesConfig");
    try {
      logger.info("Deleting languages").alsoToConsole();
      const deletedLanguages = state.deleted.languagesConfig.map((entry) => entry as number);
      if (deletedLanguages.length > 0) {
        await languageApi.deleteLanguages(deletedLanguages);
      }

      logger.info("Executing update of languages").alsoToConsole();
      const updateLanguages: LanguageShortModel[] = state.languagesConfig.languages.map((language) => {
        return {
          languageId: language.languageId,
          languageCode: language.languageCode,
          fallbackId: language.fallbackId || null
        };
      });
      await languageApi.updateLanguages(updateLanguages);

      logger.info("Setting IsTranslationStatusEnabled").alsoToConsole();
      await languageApi.updateIsTranslationStatusEnabled(state.languagesConfig.isTranslationStatusEnabled);

      const updatedLanguagesConfig: LanguagesConfig = {
        languages: state.languagesConfig.languages,
        isTranslationStatusEnabled: state.languagesConfig.isTranslationStatusEnabled
      };
      commit("set-initial-hash", { data: updatedLanguagesConfig, section: "languagesConfig" });
      // commit("set-languages-config", updatedLanguagesConfig);
      logger.info("Reloading languages in metadata store").alsoToConsole();
      await dispatch("metadata/loadLanguages", null, { root: true });
      logger.info("Finished saving the languages configuration").alsoToConsole();
    } catch (error) {
      commit("set-section-error", { section: "languagesConfig", error: error });
      logger
        .error("Saving languages configuration failed", {
          languagesConfig: state.languagesConfig,
          deletedLanguages: state.deleted.languages,
          error: error
        })
        .alsoToConsole();
    } finally {
      logger.complete();
    }
  },
  saveMediaConverterProfiles: async function ({ state, commit, dispatch, rootState }) {
    const logger = eventLogger.beginContext("saveLanguagesConfig");
    try {
      const { FileNameSmartSnippets, ...profileParameters } = state.mediaConverterProfileParameters || {};

      const payLoad: MediaConverterPayload = {
        language: rootState.metadata?.contentLanguages?.enabled[0]?.languageCode ?? "de",
        mediaConversionProfileId: state.selectedMediaConverterProfile?.mediaConversionProfileId || "",
        profileParameters: profileParameters as MediaConverterProfileParameters | null,
        fileNameSmartSnippets: FileNameSmartSnippets ?? [],
        name: state.selectedMediaConverterProfile?.name || "",
        mamRelevent: state.selectedMediaConverterProfile?.mamRelevent ?? false
      };
      const profile = await configurationManagerApi.updateMediaConverterProfile(payLoad);
      if (profile.profileId !== state.selectedMediaConverterProfile?.mediaConversionProfileId) {
        commit("update-media-converter-profile", { ...state.selectedMediaConverterProfile, mediaConversionProfileId: profile.profileId });
        commit("set-media-converter-profiles", [...(state.mediaConverterProfiles || []), state.selectedMediaConverterProfile]);
      }
      commit("set-initial-hash", { data: state.mediaConverterProfileParameters, section: "mediaConverterProfileParameters" });
      commit("set-initial-hash", { data: state.mediaConverterProfiles, section: "mediaConverterProfiles" });
      await dispatch("resetRunningPromisesIfResolved");
    } catch (error) {
      logger.error("Saving media converter profile failed", {
        mediaconerterProfiles: state.selectedMediaConverterProfile,
        error: error
      });
      commit("set-section-error", {
        section: "mediaConverterProfile",
        error: error
      });
      if (error && error instanceof AxiosError && error?.response?.data.value.toLowerCase().includes("WatermarkFilePath".toLowerCase())) {
        commit("set-field-error", {
          section: "mediaConverterProfile",
          field: "WatermarkFilePath",
          error: error
        });
      }
    }
  },
  deleteMediaConverterProfile: async function ({ state, commit, dispatch }, profileId: string) {
    try {
      await configurationManagerApi.deleteMediaConverterProfile(profileId);
      commit("update-deleted-media-converter-profile-id", profileId);
      commit("set-media-converter-profiles", state.mediaConverterProfiles);
      commit("set-initial-hash", { data: state.mediaConverterProfiles, section: "mediaConverterProfiles" });
      await dispatch("resetRunningPromisesIfResolved");
    } catch (error) {
      commit("set-section-error", {
        section: "mediaConverterProfile",
        error: error
      });
    }
  },
  copyMediaConverterProfile: async function ({ state, commit, dispatch }, args: { id: string; name: string }) {
    try {
      const fetchedResult: any = await configurationManagerApi.copyMediaConverterProfile(args.id, args.name);
      commit("set-media-converter-profiles", [...(state.mediaConverterProfiles || []), fetchedResult.copiedProfile]);
      commit("update-media-converter-profile", fetchedResult.copiedProfile);
      commit("set-initial-hash", { data: state.mediaConverterProfiles, section: "mediaConverterProfiles" });
      commit("set-media-converter-profile-parameters", { ...fetchedResult.profileParameters });
      commit("set-initial-hash", { data: fetchedResult.profileParameters, section: "mediaConverterProfileParameters" });
      await dispatch("resetRunningPromisesIfResolved");
      return fetchedResult.copiedProfile;
    } catch (error) {
      commit("set-section-error", {
        section: "mediaConverterProfile",
        error: error
      });
    }
  },
  renameMediaConverterProfile: async function ({ state, commit, dispatch }, args: { profileId: string; name: string }) {
    try {
      const promise: Promise<MediaConverterProfile> = new Promise((resolve) => {
        configurationManagerApi.renameMediaConverterProfile(args.profileId, args.name).then((response: MediaConverterProfile) => {
          resolve(response);
        });
      });
      await promise;
      commit("update-media-converter-profile-name", args);
      commit("set-initial-hash", { data: state.mediaConverterProfiles, section: "mediaConverterProfiles" });
      await dispatch("resetRunningPromisesIfResolved");
    } catch (error) {
      commit("set-section-error", {
        section: "mediaConverterProfile",
        error: error
      });
    }
  },
  setInitialHash: function ({ commit }, args: { data: any; section: string }) {
    commit("set-initial-hash", { data: args.data, section: args.section });
  },
  saveEMailSettings: async function ({ state, commit }) {
    if (state.eMailConfig && Object.entries(state.eMailConfig).length > 0) {
      try {
        await emailConfigurationApi.save(state.eMailConfig);
        commit("set-initial-hash", { data: state.eMailConfig, section: "eMailConfig" });
      } catch (error) {
        commit("set-section-error", {
          section: "eMailConfig",
          error: error
        });
      }
    }
  },
  saveProfileRules: async function ({ state, commit }) {
    try {
      const modifiedItems = Object.keys(state.initialDataHash.profileRules).filter((x) => state.initialDataHash.profileRules[x]);
      if (modifiedItems.length == 0) {
        return;
      }
      const tasks = modifiedItems.map((x) => {
        return configurationManagerApi.updateProfileRules(x, state.profileRules[x]);
      });

      await Promise.all(tasks);
      modifiedItems.forEach((x) => {
        commit("set-initial-hash", { section: "profileRules", childSectionId: x });
      });
      if (state.deleted.profileRules.length) {
        await configurationManagerApi.deleteProfileRules(state.deleted.profileRules);
      }
    } catch (error) {
      commit("set-section-error", {
        section: "profileRules",
        error: error
      });
    }
  },
  saveUnitConfig: async function ({ state, commit }) {
    try {
      if (state.unitConfig) {
        const unitsToUpdate: UnitDetail[] = state.unitConfig.filter((x) => !x.isLocked);
        const result = await configurationManagerApi.updateUnitSystems(unitsToUpdate);
        if (result) {
          commit("update-unit-systems-ids", result);
        }
        if (state.selectedUnit && state.selectedUnit.unitSystemId === Guid.empty) {
          const selectedUnit = state.unitConfig.find((x) => x.name === state.selectedUnit?.name);
          commit("set-selected-unit", selectedUnit);
        }
      }
      if (state.deleted.unitConfig.length) {
        await configurationManagerApi.deleteUnitSystems(state.deleted.unitConfig);
      }
      commit("set-initial-hash", { data: state.unitConfig, section: "unitConfig" });
    } catch (error) {
      commit("set-section-error", {
        section: "unitTranslations",
        error: error
      });
    }
  },
  saveUnitTranslationsConfig: async function ({ state, commit }) {
    try {
      if (state.unitTranslations && state.selectedUnit && !state.selectedUnit.isLocked) {
        const unitSystemId = state.selectedUnit?.unitSystemId;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const unitTranslationsToUpdate: any = state.unitTranslations.map(({ unitSystemId, ...rest }) => rest);
        const unitTranslationPayload = {
          unitSystemId: unitSystemId,
          unitSubstitution: unitTranslationsToUpdate
        };

        const result = await configurationManagerApi.updateUnitTranslations(unitTranslationPayload);
        if (result) {
          commit("update-unit-translations-ids", result);
          commit("set-selected-unit-translations", result);
        }
      }
      if (state.deleted.unitTranslations.length) {
        await configurationManagerApi.deleteUnitTranslations(state.deleted.unitTranslations);
      }
      commit("set-initial-hash", { data: state.unitTranslations, section: "unitTranslations" });
    } catch (error) {
      commit("set-section-error", {
        section: "unitTranslations",
        error: error
      });
    }
  },
  copyUnitSystem: async function ({ state, commit, dispatch, getters }, args: string) {
    try {
      const promise: Promise<UnitDetail> = new Promise((resolve) => {
        configurationManagerApi.copyUnitSystem(args).then((response: UnitDetail) => {
          resolve(response);
        });
      });
      const fetchedResult = await promise;
      if (getters.isSectionModified("unitConfig")) {
        await dispatch("saveUnitConfig");
        commit("set-initial-hash", { data: [...(state.unitConfig || []), fetchedResult], section: "unitConfig" });
      }
      commit("set-unit-config", [...(state.unitConfig || []), fetchedResult]);
      await dispatch("resetRunningPromisesIfResolved");
      commit("set-initial-hash", { data: state.unitConfig, section: "unitConfig" });
    } catch (error) {
      commit("set-section-error", {
        section: "unitTranslations",
        error: error
      });
    }
  },
  markAsDeleted: function ({ commit }, args?: any) {
    commit("mark-as-deleted", args);
  },
  setSectionError: function ({ commit }, args?: any) {
    commit("set-section-error", args);
  },
  loadAISettings: async function ({ state, rootState, commit, dispatch }, force = false) {
    if (state.artificialIntelligence == null || force) {
      const promise = configurationManagerApi.getAIConfiguration();
      commit("add-running-promise", promise);
      const fetchedResult = await promise;
      const artificialIntelligence = {
        IsAIActivated: rootState.globalParameters.IsAIActivated,
        providerConfiguration: JSON.stringify(fetchedResult.providerConfiguration, null, 2),
        promptConfiguration: JSON.stringify(fetchedResult.promptConfiguration, null, 2)
      };
      commit("set-initial-hash", {
        data: artificialIntelligence,
        section: "artificialIntelligence"
      });
      commit("set-artificial-intelligence", artificialIntelligence);
      await dispatch("resetRunningPromisesIfResolved");
    }
  },
  setAISettings: function ({ commit }, args?: ArtificialIntelligenceConfiguration) {
    commit("set-artificial-intelligence", args);
  },
  saveAISettings: async function ({ state, commit, dispatch }) {
    if (state.artificialIntelligence && Object.entries(state.artificialIntelligence).length > 0) {
      try {
        await dispatch("saveGlobalParameters", { IsAIActivated: state.artificialIntelligence.IsAIActivated }, { root: true });
        if (state.artificialIntelligence.IsAIActivated) {
          await configurationManagerApi.saveAIProviderConfiguration(JSON.stringify(state.artificialIntelligence.providerConfiguration));
          await configurationManagerApi.saveAIPromptConfiguration(JSON.stringify(state.artificialIntelligence.promptConfiguration));
        }
        await dispatch("loadAISettings", { force: true });
      } catch (error) {
        commit("set-section-error", {
          section: "artificialIntelligence",
          error: error
        });
      }
    }
  },
  checkOAuthAuthentication: async function ({ commit }) {
    try {
      await emailConfigurationApi.checkOAuthAuthentication();
      commit("set-oauth-status", {
        status: ResponseStatus.Success,
        message: "UserIsAuthenticated"
      });
    } catch (error) {
      commit("set-oauth-status", {
        status: ResponseStatus.Error,
        message: "UserNotAuthenticated"
      });
    }
  }
};

const storeModule = {
  namespaced: true,
  state: { ...defaultState() },
  getters,
  mutations,
  actions
};
export default storeModule;
